/**
 * Hand-written SEO overrides for routes whose page metadata cannot be picked up
 * by `scripts/generate-seo-route-meta.py` (service and solution hub pages that
 * are served by dynamic `$slug` routes).
 *
 * These take precedence over src/generated/seoRouteMeta.ts, so regenerating the
 * generated map never re-introduces the language default title/description.
 * Keys must be normalized paths (no trailing slash).
 */
export interface SeoRouteOverride {
  title: string;
  description: string;
  /** Optional per-route share card image (absolute URL or /public path). */
  ogImage?: string;
  ogImageAlt?: string;
}

export const SEO_ROUTE_OVERRIDES: Record<string, SeoRouteOverride> = {
  "/en/services/regulatory-toxicology": {
    title: "Regulatory Toxicology | QbD Group",
    description: "QbD Group provides regulatory toxicology: EUROTOX-certified risk assessments, PDE/OEL reports, impurity evaluations, biocompatibility and REACH support.",
  },
  "/es/servicios/toxicologia-regulatoria": {
    title: "Toxicología Regulatoria | QbD Group",
    description: "Toxicología regulatoria de QbD Group: evaluaciones de riesgo, informes PDE/OEL, impurezas y soporte REACH con expertos certificados EUROTOX.",
  },
  "/zh-cn/services/regulatory-toxicology": {
    title: "法规毒理学 | QbD Group",
    description: "QbD Group 提供法规毒理学支持：EUROTOX 认证专家开展风险评估、PDE/OEL 报告、杂质评估、生物相容性评价及 REACH 注册支持。",
  },
  "/en/services/quality-assurance/regulatory-inspection-readiness": {
    title: "Regulatory Inspection Readiness | QbD Group",
    description:
      "Mock inspections, readiness assessments, back-room support and remediation for FDA and EU/EEA inspections, towards permanent inspection readiness.",
  },
  // QA offering pages with their own hero images (hero = share card = overview card).
  "/es/servicios/quality-assurance/externalizacion-garantia-de-calidad": {
    title: "Externalización del control de calidad — Quality Assurance | QbD Group",
    description:
      "En QbD Group nos encargamos de tus actividades de Quality Assurance con un equipo dedicado de expertos. Desde la gestión de proveedores hasta la gestión de",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Foutsourced-qa-hero.jpg",
    ogImageAlt: "External QA consultant working alongside a client's quality team at a pharmaceutical site",
  },
  "/zh-cn/services/quality-assurance/qa-support-outsourcing": {
    title: "QA 外包 — Quality Assurance | QbD Group",
    description:
      "QbD Group 由专属专家团队接管您的质量保证活动。从供应商管理到偏差处理,我们提供无缝、高性价比的解决方案,让您专注于核心战略。",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Foutsourced-qa-hero.jpg",
    ogImageAlt: "External QA consultant working alongside a client's quality team at a pharmaceutical site",
  },
  "/es/servicios/quality-assurance/auditoria-calidad": {
    title: "Auditoría de calidad — Quality Assurance | QbD Group",
    description:
      "QbD Group ofrece auditores certificados y experimentados para realizar auditorías internas y de proveedores en tu nombre. Nuestros expertos en QA también p",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fgxp-audits-hero.jpg",
    ogImageAlt: "GxP auditor assessing a pharmaceutical production area together with a site representative",
  },
  "/zh-cn/services/quality-assurance/integrated-audit-solutions": {
    title: "QA 审计与整改 — Quality Assurance | QbD Group",
    description:
      "QbD Group 提供经认证且经验丰富的审计师,代表您执行内部审计与供应商审计。我们的 QA 专家还可协助您有效解决审计发现,确保合规并推动持续改进。",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fgxp-audits-hero.jpg",
    ogImageAlt: "GxP auditor assessing a pharmaceutical production area together with a site representative",
  },
  "/en/services/quality-assurance/qp-rp-prrc-services": {
    title: "QP, RP & PRRC Services | QbD Group",
    description:
      "QbD Group provides experienced Qualified Persons and Responsible Persons to help you meet mandatory requirements, maintain compliance and ensure operation",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fqp-rp-hero.jpg",
    ogImageAlt: "Qualified Person reviewing batch documentation before product release",
  },
  "/es/servicios/quality-assurance/persona-cualificada-persona-responsable": {
    title: "Persona Cualificada (PC) y Persona Responsable (PR) — Quality Assurance | QbD Group",
    description:
      "QbD Group proporciona Personas Cualificadas y Personas Responsables con experiencia para ayudarte a cumplir los requisitos obligatorios, mantener el cumpli",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fqp-rp-hero.jpg",
    ogImageAlt: "Qualified Person reviewing batch documentation before product release",
  },
  "/zh-cn/services/quality-assurance/qp-rp-prrc-services": {
    title: "合格人员 (QP) 与负责人 (RP) 服务 — Quality Assurance | QbD Group",
    description:
      "QbD Group 提供经验丰富的合格人员 (QP) 与负责人 (RP),帮助您满足强制性要求、保持合规并确保运营连续性。从批次放行到合规监控,我们的专家全方位支持您的需求。",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fqp-rp-hero.jpg",
    ogImageAlt: "Qualified Person reviewing batch documentation before product release",
  },
  "/en/services/quality-assurance/qa-consultancy-apr-pqr": {
    title: "QA Consultancy (APR & PQR) | QbD Group",
    description:
      "QbD Group offers full support for Annual Product Review and Product Quality Review — from data analysis and PQR writing to compliance review. These mandato",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fapr-pqr-hero.jpg",
    ogImageAlt: "Quality professionals analyzing product trend data for an annual product quality review",
  },
  "/es/servicios/quality-assurance/revision-anual-productos": {
    title: "Revisión Anual de la Calidad del Producto (APQR / PQR) — Quality Assurance | QbD Group",
    description:
      "QbD Group ofrece soporte integral para la Revisión Anual de Producto y la Revisión de Calidad de Producto — desde el análisis de datos y la redacción del P",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fapr-pqr-hero.jpg",
    ogImageAlt: "Quality professionals analyzing product trend data for an annual product quality review",
  },
  "/en/industries/medical-devices/md-cro-orthopaedic-spine": {
    title: "Orthopaedic & Spine Medical Device CRO | QbD Group",
    description: "Clinical evidence for orthopaedic and spine medical devices, from EU MDR strategy and clinical studies to PMCF, market access and long-term implant follow-up.",
  },
  "/es/servicios/clinical-solutions/ensayos-clinicos-de-dispositivos-medicos": {
    title: "CRO de Medical Devices: Ensayos Clínicos | QbD Group",
    description:
      "CRO full-service para ensayos clínicos de medical devices: estrategia, diseño de estudio, gestión de centros, monitorización, datos y seguimiento postcomercialización.",
  },
  "/es/servicios/clinical-solutions/estudios-de-rendimiento-clinico-de-ivd": {
    title: "CRO de IVD: Estudios de Rendimiento Clínico | QbD Group",
    description:
      "Estudios de rendimiento clínico de IVD conformes con el IVDR: estrategia, protocolos, gestión de centros, monitorización y análisis de datos con QbD IVD | Qarad.",
  },
  // Two industry pages share the "Gestión del ciclo de vida" heading; the titles
  // must name the industry so they are not duplicate title tags.
  "/es/industrias/medical-devices/gestion-del-ciclo-de-vida": {
    title: "Gestión del Ciclo de Vida de Medical Devices e IVD | QbD Group",
    description:
      "Mantén tus medical devices e IVD conformes, seguros y competitivos durante todo su ciclo de vida: vigilancia, PMS, PMCF y cambios de diseño.",
  },
  "/es/industrias/pharma-biotech/gestion-del-ciclo-de-vida": {
    title: "Gestión del Ciclo de Vida de Medicamentos | QbD Group",
    description:
      "Soporte integral para que tus medicamentos permanezcan seguros, conformes y disponibles: variaciones, renovaciones, CMC y mantenimiento regulatorio.",
  },
  "/en/knowledge-center/glossary": {
    title: "Life Sciences Regulatory Glossary | QbD Group",
    description:
      "Clear definitions of 40+ life sciences regulatory terms: EU MDR, IVDR, GMP, GCP, CSV, QPPV, CER, notified body and more, explained by QbD Group experts.",
  },
  "/es/recursos/glosario": {
    title: "Glosario Regulatorio de Life Sciences | QbD Group",
    description:
      "Definiciones claras de más de 40 términos regulatorios de Life Sciences: EU MDR, IVDR, GMP, GCP, CSV, QPPV, CER, organismo notificado y más, explicados por expertos.",
  },
  "/zh-cn/knowledge-center/glossary": {
    title: "生命科学法规术语表 | QbD Group",
    description:
      "清晰解释 40 多个生命科学法规术语：EU MDR、IVDR、GMP、GCP、CSV、QPPV、CER、公告机构等，由 QbD Group 专家撰写。",
  },
  "/en/services/clinical-solutions": {
    title: "Clinical Solutions | QbD Group",
    description:
      "End-to-end clinical solutions for pharma, biotech and medtech: clinical strategy, study operations, monitoring, data management, safety and medical writing.",
  },
  "/en/services/pharmacovigilance": {
    title: "Pharmacovigilance Services | QbD Group",
    description:
      "Pharmacovigilance services covering QPPV and local person support, safety databases, signal management, PSMF and EudraVigilance reporting under GVP.",
  },
  "/en/services/regulatory-affairs": {
    title: "Regulatory Affairs Services | QbD Group",
    description:
      "Regulatory affairs services for pharma, biotech, medical devices and IVDs: regulatory strategy, submissions, technical documentation, labeling and EU representation.",
  },
  "/es/servicios/pharmacovigilance": {
    title: "Servicios de Farmacovigilancia | QbD Group",
    description:
      "Servicios de farmacovigilancia: soporte QPPV y persona de contacto local, bases de datos de seguridad, gestión de señales, PSMF y notificación en EudraVigilance según GVP.",
  },
  "/es/soluciones/gateway-to-europe": {
    title: "Acceso al Mercado Europeo | QbD Group",
    description:
      "Solución integral para acceder al mercado europeo: estrategia regulatoria, documentación técnica, representante autorizado y soporte de calidad para entrar en la UE.",
  },
  "/es/soluciones/operational-excellence": {
    title: "Excelencia Operativa | QbD Group",
    description:
      "Excelencia operativa en ciencias de la vida: sistemas de calidad, validación, digitalización de procesos y mejora continua para operaciones GxP eficientes y conformes.",
  },
  "/es/soluciones/product-development": {
    title: "Desarrollo de Producto | QbD Group",
    description:
      "Desarrollo de producto de principio a fin: diseño y desarrollo, estrategia regulatoria, evidencia clínica y calidad para llevar tu producto al mercado con seguridad.",
  },
  // Localized titles/descriptions so /es and /zh-cn routes no longer reuse their
  // English twin's metadata (duplicate title/description tags).
  "/es/industrias/diagnostico-complementario": {
    title: "PrecisionDx (CDx): Diagnóstico Complementario | QbD Group",
    description:
      "Expertise en diagnóstico complementario y medicina de precisión: sincronizamos los flujos de trabajo de Pharma e IVD para el desarrollo conjunto fármaco-diagnóstico.",
  },
  "/es/podcasts": {
    title: "Podcasts de Life Sciences | QbD Group",
    description:
      "Escucha debates de expertos sobre estrategia regulatoria, ensayos clínicos y gestión de calidad en la industria de Life Sciences.",
  },
  "/es/politica-de-privacidad": {
    title: "Política de Privacidad | QbD Group",
    description:
      "Política de privacidad de QbD Group: cómo recogemos, usamos y protegemos tus datos personales.",
  },
  "/es/gracias/registro": {
    title: "Registro Confirmado | QbD Group",
    description:
      "Gracias por registrarte. Te enviaremos por email todos los detalles de tu inscripción.",
  },
  "/es/gracias/suscripcion": {
    title: "Suscripción Confirmada | QbD Group",
    description:
      "Gracias por suscribirte a nuestra newsletter. Recibirás insights de Life Sciences directamente en tu bandeja de entrada.",
  },
  "/zh-cn/services/clinical-solutions/clinical-evidence-support": {
    title: "临床证据支持 — Clinical Solutions | QbD Group",
    description:
      "我们提供专家支持，帮助您优化临床证据策略与医学写作，确保产品满足法规预期。",
  },
  "/zh-cn/services/clinical-solutions/clinical-pharmacovigilance-service": {
    title: "临床药物警戒服务 — Clinical Solutions | QbD Group",
    description:
      "药物警戒是识别、评估和预防不良反应的科学。QbD Group 为您的临床研究提供全面的药物警戒服务。",
  },
  "/zh-cn/services/clinical-solutions/clinical-quality-services": {
    title: "临床质量服务 — Clinical Solutions | QbD Group",
    description:
      "建立或维护临床质量管理体系（QMS）遇到困难？我们的临床团队拥有审计员与 QA 专家，提供全流程支持。",
  },
  "/zh-cn/services/clinical-solutions/clinical-regulatory-services": {
    title: "临床法规服务 — Clinical Solutions | QbD Group",
    description:
      "临床试验审批流程复杂繁琐。我们帮助您简化流程，顺利获批并按计划启动研究。",
  },
  "/zh-cn/services/clinical-solutions/clinical-safety-management": {
    title: "临床安全管理 — Clinical Solutions | QbD Group",
    description:
      "临床安全管理确保临床研究的安全性、数据完整性与伦理合规。我们在研究全周期提供支持。",
  },
  "/zh-cn/services/clinical-solutions/clinical-strategy-support": {
    title: "临床策略支持 — Clinical Solutions | QbD Group",
    description:
      "我们帮助您为医疗器械制定稳健的临床策略，提供所需的专业知识与全程指导。",
  },
  "/zh-cn/services/clinical-solutions/clinical-study-operations": {
    title: "临床试验运营 — Clinical Solutions | QbD Group",
    description:
      "临床试验运营涵盖研究的规划、执行与管理。QbD Group 专注于为您搭建并管理临床研究。",
  },
  "/zh-cn/services/clinical-solutions/data-management-and-analysis": {
    title: "临床数据管理与分析 — Clinical Solutions | QbD Group",
    description:
      "我们的临床数据管理服务覆盖数据全生命周期：从计划与 CRF 设计到数据处理、清理与统计分析。",
  },
  "/zh-cn/services/clinical-solutions/medical-writing-for-in-vitro-diagnostics": {
    title: "IVD 医学写作 — Clinical Solutions | QbD Group",
    description:
      "对于 IVD 产品，合规的临床证据文件至关重要。QbD IVD | Qarad 团队提供写作、咨询与文件编制服务。",
  },
  "/zh-cn/services/clinical-solutions/medical-writing-for-medical-devices": {
    title: "医疗器械医学写作 — Clinical Solutions | QbD Group",
    description:
      "高质量的医学写作是法规合规与患者安全的关键。我们的团队提供完整的医学写作服务。",
  },
  "/zh-cn/services/clinical-solutions/pmcf": {
    title: "上市后临床跟踪（PMCF） — Clinical Solutions | QbD Group",
    description:
      "我们以量身定制、符合 MDR 要求的 PMCF 策略，帮助您将法规义务转化为临床与商业价值。",
  },
  "/zh-cn/services/clinical-solutions/pmpf": {
    title: "上市后性能跟踪（PMPF） — Clinical Solutions | QbD Group",
    description:
      "在 QbD IVD | Qarad，我们帮助您将 PMPF 转化为结构化、可落地的流程，保障合规与患者安全。",
  },
  "/zh-cn/services/lab-services/rd-projects": {
    title: "研发项目 — Lab Services | QbD Group",
    description:
      "QbD Group 依托深厚的科学专长与先进技术，为制药研发项目提供量身定制的实验室解决方案。",
  },
  "/zh-cn/services/lab-services/residual-solvent-testing-gc": {
    title: "残留溶剂检测（GC） — Lab Services | QbD Group",
    description:
      "采用经验证的 GC-FID 与 GC-MS 方法识别并定量生产后的微量有机溶剂，确保符合法规要求。",
  },
  "/zh-cn/services/lab-services/stability-studies": {
    title: "稳定性研究 — Lab Services | QbD Group",
    description:
      "QbD Group 以符合 ICH 要求的严格稳定性研究与先进设施，确保您的产品达到最高质量标准。",
  },
  "/es/servicios/farmacovigilancia/informes-seguridad": {
    title: "Informes de Seguridad y Detección de Señales — Pharmacovigilance | QbD Group",
    description:
      "Los informes de seguridad son clave en farmacovigilancia y vigilancia postcomercialización. En QbD Group analizamos y elaboramos informes que aseguran el cumplimiento.",
  },
  "/es/servicios/farmacovigilancia/servicios-integrales-de-informacion-medica": {
    title: "Servicios Integrales de Información Médica — Pharmacovigilance | QbD Group",
    description:
      "La información médica precisa y puntual es un pilar de la seguridad del paciente. QbD Group combina expertise científico y operativo para gestionarla end-to-end.",
  },
  "/zh-cn/services/pharmacovigilance/eudravigilance-services": {
    title: "EudraVigilance 药品字典（XEVMPD） — Pharmacovigilance | QbD Group",
    description:
      "借助 QbD 的专业能力轻松实现 XEVMPD 合规，简化法规提交并持续维护最新的安全性信息。",
  },
  "/zh-cn/services/pharmacovigilance/literature-monitoring": {
    title: "文献监测与预警服务 — Pharmacovigilance | QbD Group",
    description:
      "通过 QbD Group 的文献监测服务保持合规：系统化检索安全性信号，确保及时报告并强化警戒策略。",
  },
  "/zh-cn/services/pharmacovigilance/project-management": {
    title: "项目管理 — Pharmacovigilance | QbD Group",
    description:
      "借助 QbD 的项目管理专长顺畅协调药物警戒项目：达成安全目标、满足合规要求并提升运营效率。",
  },
  "/es/servicios/farmacovigilancia/auditorias-fv-inspecciones-qms": {
    title: "Auditorías de Farmacovigilancia (FV) e Inspecciones — Pharmacovigilance | QbD Group",
    description:
      "En QbD Group realizamos auditorías de FV certificadas, inspecciones simuladas y actividades ISO para que tu sistema cumpla las Buenas Prácticas de Farmacovigilancia.",
  },
  "/es/servicios/farmacovigilancia/servicios-qms-psmf": {
    title: "Servicios de QMS y PSMF de Farmacovigilancia — Pharmacovigilance | QbD Group",
    description:
      "Cumplir la normativa nacional e internacional de farmacovigilancia exige sistemas robustos y documentación rigurosa. En QbD Group creamos y mantenemos tu QMS y PSMF.",
  },
  "/es/servicios/farmacovigilancia/inteligencia-regulatoria": {
    title: "Inteligencia Regulatoria — Pharmacovigilance | QbD Group",
    description:
      "En un entorno regulatorio en constante cambio, nuestro servicio de Inteligencia Regulatoria te mantiene al día de la legislación internacional y local aplicable.",
  },
  "/zh-cn/services/pharmacovigilance/safety-database-solution": {
    title: "药物不良反应追踪 — Pharmacovigilance | QbD Group",
    description:
      "精准监测并处理药物不良反应。QbD Group 的安全数据库解决方案在保障合规的同时兼顾运营效率。",
  },
  "/zh-cn/services/pharmacovigilance/signal-detection-management": {
    title: "安全性信号管理项目 — Pharmacovigilance | QbD Group",
    description:
      "通过 QbD Group 的安全性信号项目提升患者安全：及早发现风险、确保合规并建立信任。",
  },
  "/zh-cn/services/pharmacovigilance/vigilance-pharmacovigilance-hotline-services": {
    title: "警戒与药物警戒热线服务 — Pharmacovigilance | QbD Group",
    description:
      "突发事件不会休息，我们的支持也不会。One Vigilance 热线全年 7×24 小时提供专属药物警戒支持。",
  },
  "/es/servicios/qualification-validation/commissioning": {
    title: "Commissioning de Sistemas y Equipos — Qualification & Validation | QbD Group",
    description:
      "El commissioning es clave para que tus sistemas, utilities, instalaciones y equipos funcionen al máximo rendimiento y con total seguridad. En QbD Group somos expertos.",
  },
  "/zh-cn/services/qualification-validation/commissioning": {
    title: "系统与设备调试（Commissioning） — Qualification & Validation | QbD Group",
    description:
      "Commissioning 对系统与设备实现最佳性能并保持安全至关重要。QbD Group 专注于确保您的系统、utilities、设施与设备正常运行。",
  },
  "/zh-cn/services/quality-assurance/qa-consultancy-apr-pqr": {
    title: "年度产品回顾（APR）与产品质量回顾（PQR） — Quality Assurance | QbD Group",
    description:
      "QbD Group 提供 APR 与 PQR 全流程支持：数据分析、报告撰写与合规审阅，帮助您履行强制性回顾义务。",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fapr-pqr-hero.jpg",
    ogImageAlt: "Quality professionals analyzing product trend data for an annual product quality review",
  },
  "/es/servicios/regulatory-affairs/cumplimiento-ivdr-certificacion-ce": {
    title: "Cumplimiento del IVDR y Certificación CE — Regulatory Affairs | QbD Group",
    description:
      "El Reglamento IVDR (UE) 2017/746 ha transformado el acceso de los IVD al mercado europeo. QbD Group acompaña a los fabricantes en todo el proceso de certificación CE.",
  },
  "/zh-cn/services/regulatory-affairs/ivdr-compliance-ce-certification": {
    title: "IVDR 合规与 CE 认证 — Regulatory Affairs | QbD Group",
    description:
      "欧盟体外诊断法规 (EU) 2017/746 彻底改变了 IVD 进入欧洲市场的方式。QbD Group 为制造商提供全流程支持。",
  },
  "/es/servicios/software-solutions-services/ifucare": {
    title: "IFUcare: IFU Electrónico (eIFU) — Software Solutions & Services | QbD Group",
    description:
      "IFUcare ofrece un servicio de IFU electrónico para fabricantes de medical devices e IVD: deja atrás el papel y cumple con los requisitos de eIFU.",
  },
  "/zh-cn/services/software-solutions-services/operational-software-compliance": {
    title: "运营软件合规 — Software Solutions & Services | QbD Group",
    description:
      "确保您的运营与业务软件在 GxP 环境中持续保持合规、受控且经过验证。",
  },
  "/zh-cn/services/software-solutions-services/paperless-validation": {
    title: "无纸化验证 — Software Solutions & Services | QbD Group",
    description:
      "通过无纸化验证提升效率：数字化执行验证流程，缩短周期并提高数据完整性。",
  },
  "/es/servicios/software-solutions-services/scilife": {
    title: "Scilife™: Software de Gestión de Calidad — Software Solutions & Services | QbD Group",
    description:
      "Implementa y valida Scilife™, la plataforma de gestión de calidad (QMS) para Life Sciences, con el acompañamiento de los expertos de QbD Group.",
  },
  "/zh-cn/services/software-solutions-services/software-implementations": {
    title: "软件实施 — Software Solutions & Services | QbD Group",
    description:
      "从选型到验证与上线，QbD Group 为 GxP 环境中的软件实施项目提供端到端支持。",
  },
  // CTR/ranking optimization for the CSV pillar page (26k+ impressions, avg pos ~12):
  // lead with the GAMP 5 query intent and a benefit-led snippet.
  "/en/a-complete-guide-to-computer-system-validation": {
    title: "Computer System Validation (CSV) Guide: GAMP 5 Explained | QbD Group",
    description:
      "What is computer system validation? Learn GAMP 5 categories, the V-model, risk-based validation and data integrity for GxP systems in pharma, biotech and MedTech.",
  },
  // Offering renamed to "QA Support & Outsourcing" (2026-10-30): the generated
  // map still carries the old name, so pin the new meta title here.
  "/en/services/quality-assurance/qa-support-outsourcing": {
    title: "QA Support & Outsourcing | QbD Group",
    description:
      "At QbD Group, we take over your quality assurance activities with a dedicated team of experts. From supplier management to deviation handling, we provide seamless, cost-effective solutions so you can focus on your core strategy.",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Foutsourced-qa-hero.jpg",
    ogImageAlt: "External QA consultant working alongside a client's quality team at a pharmaceutical site",
  },
  // Offering renamed to "Integrated Audit Solutions" (2026-10-30): the generated
  // map still carries the old name, so pin the new meta title here.
  "/en/services/quality-assurance/integrated-audit-solutions": {
    title: "Integrated Audit Solutions | QbD Group",
    description:
      "QbD Group offers certified and experienced auditors to perform internal and supplier audits on your behalf. Our QA experts can also support you in resolving audit findings effectively, ensuring compliance and driving improvement.",
    ogImage: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance%2Fheroes%2Fgxp-audits-hero.jpg",
    ogImageAlt: "GxP auditor assessing a pharmaceutical production area together with a site representative",
  },
};
